import { useState } from "react";
import { CreditCard, X } from "lucide-react";
import { money } from "./orderApi";
import { mockPay, openRazorpay, verifyPayment } from "./razorpay";

const errText = (e, fb) => { const d = e?.response?.data?.detail; return (d && (d.message || (typeof d === "string" ? d : null))) || e?.message || fb; };

/** Drives one payment attempt for an awaiting_payment order: real Razorpay Checkout, or the MOCKED simulator in Preview. */
export const PayOnlineSheet = ({ slug, requestId, trackingToken, intent, brand, customer, color, onPaid, onCancel }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const finish = async (r) => {
    setBusy(true); setError("");
    try { const v = await verifyPayment(slug, requestId, trackingToken, r); onPaid(v); }
    catch (e) { setError(errText(e, "Payment could not be verified. If money was deducted it will be reconciled automatically.")); }
    finally { setBusy(false); }
  };
  const payReal = async () => {
    setBusy(true); setError("");
    try { const r = await openRazorpay({ intent, brand, customer }); await finish(r); }
    catch (e) { setError(e?.code === "dismissed" ? "Payment not completed. You can retry or pay at the outlet." : errText(e, "Payment failed. Please retry.")); setBusy(false); }
  };
  const payMock = async (method) => {
    setBusy(true); setError("");
    try { await finish(await mockPay(slug, requestId, trackingToken, method)); }
    catch (e) { setError(errText(e, "Simulated payment failed.")); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center bg-black/60" data-testid="pay-online-sheet">
      <div className="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2"><CreditCard size={18} />Pay {money(intent.amount_paise / 100)}</h2>
          <button type="button" onClick={onCancel} disabled={busy} className="p-2 rounded-full bg-slate-100" data-testid="pay-online-close"><X size={16} /></button>
        </div>
        {intent.mode === "test" && <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2" data-testid="pay-online-test-banner">TEST MODE — no real money is charged.</div>}
        {intent.mock ? (
          <div className="space-y-2" data-testid="pay-online-mock">
            <p className="text-xs text-slate-500">Simulated gateway (Preview). Choose how the customer “paid”:</p>
            <div className="grid grid-cols-2 gap-2">
              {["upi", "card", "netbanking", "wallet"].map((m) => (
                <button key={m} type="button" disabled={busy} onClick={() => payMock(m)} className="rounded-2xl border border-slate-200 py-3 text-sm font-semibold uppercase hover:border-slate-900 disabled:opacity-50" data-testid={`pay-online-mock-${m}`}>{m}</button>
              ))}
            </div>
          </div>
        ) : (
          <button type="button" onClick={payReal} disabled={busy} className="w-full rounded-full text-white py-3 text-sm font-semibold disabled:opacity-50" style={{ background: color }} data-testid="pay-online-open">
            {busy ? "Processing…" : "Pay with Razorpay"}
          </button>
        )}
        {busy && <p className="text-xs text-slate-500 text-center" data-testid="pay-online-busy">Confirming payment with the gateway…</p>}
        {error && <p className="text-xs text-rose-600" data-testid="pay-online-error">{error}</p>}
        <button type="button" onClick={onCancel} disabled={busy} className="w-full text-xs text-slate-500 underline" data-testid="pay-online-later">Pay later / choose Pay at Outlet</button>
      </div>
    </div>
  );
};
